import React, { useContext, useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import {
  Button,
  Chip,
  Dialog,
  Icon,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader
} from '@clientbase/clientbase-library'
import {
  Box,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography
} from '@mui/material'
import { DataService } from 'api/DataService'
import copy from 'copy-to-clipboard'
import { InvoiceOpenIndicator } from 'features/invoice-timeline'
import { posExpiration } from 'models/Charge/NotificationDetail'
import { constructorInvoice, InvoiceParams } from 'models/Invoice'
import moment from 'moment'
import { NFeService } from 'services/NFeService'
import {
  MarkAsPaidBodyModal,
  SubmitMarkAsPaidHandler
} from 'templates/Charge/Modals/MarkAsPaidBodyModal'
import { buildZeroAmountNfseContext } from 'templates/Charge/Modals/markAsPaidNfse.utils'
import { getChargeEnum } from 'templates/v2/Products/Page/Products.utils'

import Can from 'components/Can'
import CanLogical from 'components/CanLogical'
import IconContainer from 'components/IconContainer/IconContainer'
import ModalEditChargeUnified from 'components/Modal/ModalEditChargeUnified'
import ModalEmitNfseForCharge, {
  buildNfseChargeFromInvoice,
  canEmitManualNfse
} from 'components/Modal/ModalEmitNfseForCharge'
import { Record as CustomerChargeRecord } from 'components/Modal/ModalInvoice/components/Table/Table'
import ReverseForm from 'components/ReverseForm'

import { currency } from 'utils'
import { toastProps } from 'utils/types/toast-props'

import { theme } from 'styles/theme'

import {
  headerGridMobileSx,
  mobile,
  modalHeaderMobileSx
} from '../modalHeaderMobile.styles'
import ModalInvoiceDetailsContent from '../ModalInvoiceDetails/ModalInvoiceDetailsContent'
import ModalRecurrenceDetail from '../ModalRecurrenceDetail'
import Actions from './components/Actions'
import Attemps from './components/Attemps'
import BillingDetails from './components/BillingDetails'
import CancelConfirmationModal from './components/CancelConfirmationDialog/CancelConfirmationDialog'
import Composition from './components/Composition'
import Historic from './components/Historic'
import InternalIdentifier from './components/InternalIdentifier'
import Loading from './components/Loading'
import NegativateDetail from './components/NegativateDetail'
import NFe from './components/NFe'
import Nfses from './components/Nfses'
import NotificationDetails from './components/NotificationDetails'
import PaymentDetails from './components/PaymentDetails'
import { ModalChargeDetailContext } from './context'
import { getHoverText, getStatus } from './ModalChargeDetail.utils'

interface DetailChargeBodyModalParams {
  uuid: string
  onClose: (reloadTable?: boolean) => void
  setReloadTable?: React.Dispatch<React.SetStateAction<boolean>>
}

const ModalChargeDetailBody = ({
  uuid,
  onClose,
  setReloadTable
}: DetailChargeBodyModalParams) => {
  const {
    updateInvoice,
    updatePaids,
    updatePaymentType,
    updateModal,
    updateTab,
    updateNfe,
    updateReload,
    updateDialog,
    updateDialogReverse,
    dialogReverse,
    modal,
    upload,
    invoice,
    dialog,
    updateModalNfDetails,
    modalNfDetails,
    updateAnchorEl,
    reload,
    scrollToNotifications
  } = useContext(ModalChargeDetailContext)

  const [loadingModal, setLoadingModal] = useState<boolean>(true)

  // Cobrança recém-baixada com "Emitir nota fiscal" marcado: abre a emissão de
  // nota já vinculada a ela, com o valor pago.
  const [nfseAfterReceipt, setNfseAfterReceipt] = useState<{
    charge: CustomerChargeRecord
    amount: string
  } | null>(null)

  const canManageNfses = !!CanLogical({ permission: 'nfses:manage' })
  const canEmitNfseOnReceipt = canManageNfses && canEmitManualNfse(invoice)

  const handleReloadTable = () => {
    setReloadTable && setReloadTable((prevState) => !prevState)
  }

  //Verificar
  const handleCloseModalNFDetails = () => {
    updateModalNfDetails(false)
  }

  const getInvoice = async (uuid: string) => {
    try {
      setLoadingModal(true)
      // SC-11320: detalhe autenticado do merchant. O `/invoice` é a fatura
      // pública do pagador e não traz histórico, anotações nem taxas.
      const { data } = await DataService({
        type: 'GET',
        url: `/v1/billings/${uuid}/details`
      })
      const invoice = constructorInvoice(data.record)

      const types = invoice?.paymentType
        .split(';')
        .filter((type) => type !== 'boleto_pix')
        .map((type) => {
          return getChargeEnum(type)
        })

      const paymentPaid = invoice?.payments.filter(
        (payment) => payment.status === 'paid'
      )

      updatePaymentType(types.filter(Boolean))
      updatePaids(paymentPaid)
      updateInvoice(invoice)
      setLoadingModal(false)
      checkExpiration(invoice)
    } catch (err) {
      toast.error('Erro ao carregar a cobrança.')
    }
  }

  // Cancela o agendamento da nota antes de um pagamento de valor zero. Precisa
  // vir ANTES do POST /pay: depois que a billing é paga o worker cria a nota e
  // NfseScheduled::Cancel passa a recusar o cancelamento.
  const handleCancelScheduledNfse = async (): Promise<boolean> => {
    const scheduledUuid = invoice?.nfsesScheduled?.uuid

    if (!scheduledUuid) return false

    const response = await DataService({
      type: 'DELETE',
      url: `/v1/nfse_scheduleds/${scheduledUuid}`
    })

    if (response.error || response.status >= 400) {
      toast.error(
        'Não foi possível cancelar o agendamento da nota. A cobrança NÃO foi paga.',
        toastProps
      )
      return false
    }

    return true
  }

  const submitInvoice: SubmitMarkAsPaidHandler = async (dataForm, options) => {
    const id = toast.loading('Registrando cobrança como paga...')
    const { amountPaid, comment, datePaid, directType, source } = dataForm
    const nfseScheduledCancelled = !!options?.nfseScheduledCancelled

    const data = {
      amount_paid: '',
      comment,
      date_paid: moment(datePaid).format('YYYY-MM-DD'),
      direct_type: directType.value,
      // Opcional: sem conta escolhida a chave nem vai, e a baixa segue como antes.
      ...(source?.value && { accounts_payable_source_uuid: source.value })
    }

    if (amountPaid) {
      const value = currency(amountPaid).replace(/\./g, '').replace(',', '.')
      data.amount_paid = value
    }

    const response = await DataService({
      data,
      type: 'POST',
      url: `/v1/billings/${uuid}/pay`
    })

    // O agendamento já foi cancelado neste ponto: a mensagem precisa deixar
    // claro que só o pagamento falhou, e que basta tentar de novo.
    const errorMessage = nfseScheduledCancelled
      ? 'O agendamento da nota fiscal foi cancelado, mas a cobrança NÃO foi paga. Tente registrar o recebimento novamente.'
      : response.error && (response.detail_error || response.message)

    const successMessage = nfseScheduledCancelled
      ? 'Recebimento registrado e agendamento de emissão da nota fiscal cancelado.'
      : response.data?.message

    toast.update(id, {
      render: response.error ? errorMessage : successMessage,
      type: response.error ? 'error' : 'success',
      ...toastProps
    })

    if (response.status === 200) {
      const ref = upload?.current
      ref?.state?.uploadedFiles.forEach((file) =>
        ref.processUpload(file, response?.data?.record?.uuid)
      )

      handleCloseMarkAsPaid()

      const nfseCharge =
        dataForm.emitNfse && canEmitNfseOnReceipt
          ? buildNfseChargeFromInvoice(invoice, {
              status: 'paid',
              amount_paid: data.amount_paid,
              date_paid: data.date_paid
            })
          : undefined

      if (nfseCharge) {
        // O recarregamento do detalhe troca o conteúdo pelo skeleton e
        // desmontaria o modal da nota no meio do preenchimento: ele fica para
        // quando a nota for emitida ou o modal fechado.
        setNfseAfterReceipt({ charge: nfseCharge, amount: data.amount_paid })
      } else {
        setTimeout(() => {
          getInvoice(uuid)
        }, 2500)
      }
    }
    return false
  }

  const handleCloseNfseAfterReceipt = () => {
    setNfseAfterReceipt(null)
    updateReload((prevState) => !prevState)
  }

  const getNFdetails = async () => {
    const detail = await NFeService.getNFeDetails(invoice.nfse_uuid as string)
    updateNfe(detail)
  }

  useEffect(() => {
    // `nfsesScheduled` diz que existe agendamento, não que a nota já foi
    // emitida — o uuid da nota só aparece depois da emissão. Buscar pelo
    // agendamento chamava GET /v1/nfses/null a cada atualização da fatura.
    if (invoice?.nfse_uuid) {
      getNFdetails()
    }
  }, [invoice])

  const checkExpiration = (invoice: InvoiceParams) => {
    const dateBasedCampaigns = ['NFSe emitida']
    const hasProExpiration = invoice?.messageTracking?.some((item) => {
      if (posExpiration.includes(item.campaign)) return true
      if (
        dateBasedCampaigns.includes(item.campaign) &&
        invoice?.dueDate &&
        item.dateSent
      ) {
        const sentDate = moment(item.dateSent).startOf('day')
        const dueDate = moment(invoice.dueDate).startOf('day')
        return sentDate.isAfter(dueDate)
      }
      return false
    })
    updateTab(hasProExpiration ? 1 : 0)
  }

  function handleConfirmCancelModalClose() {
    updateModal((prevState) => ({
      ...prevState,
      isOpenConfirmCancelModal: false
    }))
  }

  const handleCloseDetailRecurrence = (reload?: boolean) => {
    updateModal((s) => ({
      ...s,
      uuidRecurrenceDetail: '',
      isOpenModalRecurrenceDetail: false
    }))

    !!reload && updateReload((prevState) => !prevState)
  }

  function handleCloseDialogAntecipate() {
    updateModal((prevState) => ({
      ...prevState,
      isOpenDialogAntecipate: false,
      uuid: ''
    }))
  }

  async function handleDialogAntecipateConfirm() {
    updateModal((prevState) => ({ ...prevState, loading: true }))

    const id = toast.loading('Antecipando a fatura...')

    const response = await DataService({
      type: 'GET',
      url: `/v1/billings/${modal.uuid}/antecipate`
    })

    const errorMessage =
      response.error && (response.detail_error || response.message)

    toast.update(id, {
      render: response.error ? errorMessage : response.data.message,
      type: response.error ? 'error' : 'success',
      ...toastProps
    })

    updateModal((prevState) => ({
      ...prevState,
      isOpenDialogAntecipate: false,
      loading: false,
      uuid: ''
    }))

    if (!response.error) {
      !!setReloadTable && setReloadTable((s) => !s)
      reloadCharge()
    }
  }

  function handleCloseMarkAsPaid() {
    updateModal((prevState) => ({
      ...prevState,
      isOpenModalPaid: false
    }))
    !!setReloadTable && setReloadTable((s) => !s)
  }

  const handleCloseEditCharge = (refresh?: boolean) => {
    updateModal((prevState) => ({ ...prevState, isOpenEditCharge: false }))
    // Se refresh for true, atualiza os dados do modal de detalhes
    if (refresh) {
      reloadCharge()
    }
  }

  const reloadCharge = () => {
    updateReload((prevState) => !prevState)
  }

  const handleCloseReverseForm = (success?: boolean) => {
    updateDialog((s) => ({ ...s, isOpenDialog: false }))
    updateDialogReverse((prev) => ({ ...prev, isOpen: false }))
    !!success && updateDialogReverse({ isOpen: false, uuid: '' })
    !!success && updateReload((prevState) => !prevState)
  }

  const handleCloseCancelManuallyReceiptDialog = () => {
    updateDialog({
      isOpenDialog: false,
      action: () => ({}),
      description: '',
      title: '',
      icon: 'info',
      actionLabel: '',
      cancelLabel: ''
    })
  }

  useEffect(() => {
    if (uuid) getInvoice(uuid)
  }, [uuid, reload])

  const copyUuid = () => {
    copy(invoice.uuid)
    toast.success('Identificador copiado para a área de transferência')
  }

  if (loadingModal) {
    // Dentro do ModalBody/ModalHeader: retornar só o <Loading /> deixava o modal
    // sem título e sem botão de fechar enquanto os dados não chegavam — quem
    // abria por engano ficava sem saída a não ser o clique fora.
    return (
      <ModalBody>
        <ModalHeader onClose={onClose} sx={modalHeaderMobileSx}>
          <Stack
            width="100%"
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            gap={1}
            sx={{
              [mobile]: { flexDirection: 'column', alignItems: 'stretch' }
            }}
          >
            <Stack direction="row" alignItems="center" gap={2}>
              <IconContainer icon="event" />
              <Typography variant="mdBold">Detalhes da cobrança</Typography>
            </Stack>
            {/* Reserva a mesma faixa do chip de status, senão o título desliza
                para a direita quando os dados chegam. */}
            <Skeleton
              height="25px"
              variant="rounded"
              sx={{ width: '160px', [mobile]: { width: '100%' } }}
            />
          </Stack>
        </ModalHeader>
        <ModalContent>
          <Loading />
        </ModalContent>
      </ModalBody>
    )
  }

  return (
    <ModalBody>
      <ModalHeader onClose={onClose} sx={modalHeaderMobileSx}>
        {/* Mesmo arranjo do detalhe da recorrência: título à esquerda, chip de
            status e "Mais opções" à direita. Eles ficavam na primeira linha do
            corpo, competindo com os botões de ação. */}
        <Stack
          width="100%"
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          gap={1}
          sx={headerGridMobileSx}
        >
          <Tooltip
            title={`Identificador: ${invoice.uuid}`}
            placement="top"
            onClick={copyUuid}
            arrow
            PopperProps={{
              onClick: copyUuid,
              sx: { cursor: 'pointer' },
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [40, 0]
                  }
                }
              ]
            }}
          >
            <Box sx={{ [mobile]: { gridArea: 'title', minWidth: 0 } }}>
              <Stack
                sx={{ cursor: 'pointer' }}
                direction="row"
                alignItems="center"
                gap={2}
              >
                <IconContainer icon="event" />
                {/* SC-10890: no celular o título disputava a linha com o chip de
                    status e os botões, e quebrava em três linhas ("Detalhes /
                    da / cobrança"), esticando o cabeçalho. Abaixo de 620px ele
                    recua de 16px para 14px e para de quebrar. */}
                <Typography
                  variant="mdBold"
                  sx={{
                    whiteSpace: 'nowrap',
                    [theme.breakpoints.down(620)]: { fontSize: '14px' },
                    [theme.breakpoints.down(420)]: { fontSize: '13px' }
                  }}
                >
                  Detalhes da cobrança
                </Typography>
              </Stack>
            </Box>
          </Tooltip>

          {/* No celular este grupo some do layout (display: contents) para que
              status e "Mais opções" caiam em linhas diferentes do grid. */}
          <Stack
            direction="row"
            alignItems="center"
            gap={1}
            sx={{ [mobile]: { display: 'contents' } }}
          >
            {/* Leitura da fatura à esquerda do status: as duas informações
                respondem "em que pé está a cobrança" e agora ficam juntas.
                SC-10890: o ícone era só tooltip; agora leva ao "Histórico da
                cobrança" e o tooltip informa quantas vezes o cliente abriu. */}
            <Stack
              direction="row"
              alignItems="center"
              gap={1}
              sx={{ [mobile]: { gridArea: 'status' } }}
            >
              <InvoiceOpenIndicator
                variant="header"
                viewed={invoice?.viewed}
                viewsCount={invoice?.viewsCount}
                lastViewedAt={invoice?.lastViewedAt}
                onClick={scrollToNotifications}
              />
              <Tooltip
                arrow
                title={getHoverText(invoice.status, invoice.dueDate).text}
              >
                <Box sx={{ [mobile]: { flex: 1, minWidth: 0 } }}>
                  <Chip
                    key={invoice?.status}
                    label={getStatus(invoice?.status, invoice.dueDate)?.label}
                    sx={{
                      background: getStatus(invoice?.status, invoice.dueDate)
                        ?.color,
                      color: getStatus(invoice?.status, invoice.dueDate)
                        ?.fontColor,
                      height: '25px',
                      width: '160px',
                      [theme.breakpoints.down(508)]: {
                        width: '120px'
                      },
                      [mobile]: { width: '100%' }
                    }}
                  />
                </Box>
              </Tooltip>
            </Stack>
            <Can permission="billings:show">
              <Tooltip title="Mais opções">
                <IconButton
                  onClick={(event) => updateAnchorEl(event.currentTarget)}
                  size="small"
                  sx={{ [mobile]: { gridArea: 'more' } }}
                >
                  <Icon icon="moreHoriz" colorSX="black" />
                </IconButton>
              </Tooltip>
            </Can>
          </Stack>
        </Stack>
      </ModalHeader>

      <ModalContent>
        {/* mt menor e gap apertado: o header (chip + ações) é uma barra de
            controle, não um bloco de conteúdo — respirar como os accordions
            empurrava o resumo para fora da primeira tela. */}
        <Stack gap={1} mt={1}>
          <BillingDetails
            updateReload={updateReload}
            reload={reload}
            setReloadTable={setReloadTable}
          />
          <Composition />
          <NotificationDetails />
          <PaymentDetails />
          <NegativateDetail />
          <Nfses />
          <Historic />
          <NFe />
          <InternalIdentifier />
          <Attemps />
        </Stack>
      </ModalContent>
      <ModalFooter>
        <Actions />
      </ModalFooter>
      {Object.keys(invoice).length > 1 && (
        <ModalEditChargeUnified
          isOpenModalEditCharge={modal.isOpenEditCharge}
          handleCloseModal={handleCloseEditCharge}
          uuid={uuid}
          charge={invoice}
          reloadCharge={reloadCharge}
          setReloadTable={setReloadTable}
        />
      )}
      {modal.isOpenModalRecurrenceDetail && (
        <ModalRecurrenceDetail
          isOpenModalRecurrenceDetail={modal.isOpenModalRecurrenceDetail}
          handleCloseDetailRecurrence={() => handleCloseDetailRecurrence(false)}
          uuid={modal.uuidRecurrenceDetail}
        />
      )}

      <Dialog
        icon="info"
        title="ANTECIPAR FATURA"
        description="Confirma o adiantamento dessa fatura?"
        setIsOpenDialog={handleCloseDialogAntecipate}
        isOpenDialog={modal.isOpenDialogAntecipate}
      >
        <Box display="flex" alignItems="center" gap={1} width="100%">
          <Button onClick={handleCloseDialogAntecipate} fullWidth>
            Não
          </Button>
          <Button
            disabled={modal.loading}
            onClick={handleDialogAntecipateConfirm}
            fullWidth
          >
            Sim
          </Button>
        </Box>
      </Dialog>
      <Modal
        disablePortal={false}
        handleClose={handleCloseMarkAsPaid}
        open={modal.isOpenModalPaid}
        height="auto"
        width="auto"
        maxHeight="90vh"
      >
        <MarkAsPaidBodyModal
          amountBilled={invoice?.amountBilled}
          customerName={invoice?.customer?.name || ''}
          dueDate={invoice?.dueDate}
          nfseContext={buildZeroAmountNfseContext(invoice)}
          onCancelScheduledNfse={handleCancelScheduledNfse}
          onCloseModal={handleCloseMarkAsPaid}
          submitInvoice={submitInvoice}
          uploadRef={upload}
          uuid={invoice?.customer?.uuid || ''}
          canEmitNfse={canEmitNfseOnReceipt}
        />
      </Modal>

      {nfseAfterReceipt && (
        <ModalEmitNfseForCharge
          open={true}
          onClose={handleCloseNfseAfterReceipt}
          charge={nfseAfterReceipt.charge}
          initialAmount={nfseAfterReceipt.amount}
        />
      )}

      <Modal
        open={dialogReverse.isOpen}
        handleClose={() => updateDialogReverse({ isOpen: false, uuid: '' })}
        height="90vh"
        maxHeight="280px"
      >
        <ReverseForm
          uuid={dialogReverse.uuid}
          handleClose={handleCloseReverseForm}
          setDialog={updateDialog}
          setReloadTable={setReloadTable}
        />
      </Modal>

      <Dialog
        icon={dialog.icon}
        title={dialog.title}
        description={dialog.description}
        setIsOpenDialog={handleCloseCancelManuallyReceiptDialog}
        isOpenDialog={dialog.isOpenDialog}
        cancelButton
        cancelButtonLabel={dialog.cancelLabel || 'Cancelar'}
      >
        <Button
          onClick={() => {
            setLoadingModal(true)
            dialog.action()
            setLoadingModal(false)
          }}
          fullWidth
          disabled={loadingModal}
        >
          {dialog.actionLabel || 'Sim'}
        </Button>
      </Dialog>
      <Modal
        handleClose={handleCloseModalNFDetails}
        open={modalNfDetails}
        width="790px"
      >
        <ModalInvoiceDetailsContent
          uuid={invoice.nfse_uuid as string}
          handleReloadTable={handleReloadTable}
          handleCloseModal={handleCloseModalNFDetails}
        />
      </Modal>

      <CancelConfirmationModal
        cancellationData={invoice.cancellationReason}
        cancelableNfses={invoice.cancelableNfses}
        isOpen={modal.isOpenConfirmCancelModal}
        onClose={handleConfirmCancelModalClose}
        billingUuid={invoice.uuid}
        onSuccess={() => {
          reloadCharge()
          setReloadTable && setReloadTable((s) => !s)
        }}
        type="charge"
      />
    </ModalBody>
  )
}

export default ModalChargeDetailBody
